import type {QueuedLogRecord} from "@apps/qset-log-batcher/policy";

export type QsetLogFeedbackRecord = QueuedLogRecord;

export interface QsetLogPathInput {
  dbUrl: string;
  slug: string;
  userId: string;
  flushFailed?: boolean;
}

/**
 * The "Qset log:" line of a feedback report: where the log lives, never the
 * log itself (#1249). A report used to carry up to ~24 kB of events, and
 * neither the feedback endpoint's nor its mailer's size limit was ever
 * checked; the RTDB node already holds everything the batcher accepted, so
 * the report only has to say which node to read. The database URL is part of
 * it because the same path exists on dev and prod.
 */
export const buildQsetLogPathBlock = ({
  dbUrl,
  slug,
  userId,
  flushFailed = false,
}: QsetLogPathInput): string => {
  const path = `${dbUrl.replace(/\/+$/, "")}/results-logs/${slug}/${userId}`;
  return flushFailed
    ? `Qset log: ${path} (flush failed: the newest events may be missing)`
    : `Qset log: ${path}`;
};

export const unavailableBlock = (reason: string): string =>
  `Qset log: unavailable (${reason})`;

/** The `data.questionId` of the last `question-enter` record, if any. */
export const lastQuestionId = (
  records: ReadonlyArray<QsetLogFeedbackRecord>,
): string | undefined => {
  for (let i = records.length - 1; i >= 0; i -= 1) {
    const record = records[i];
    if (record.message === "question-enter") {
      const questionId = record.data?.questionId;
      return typeof questionId === "string" ? questionId : undefined;
    }
  }
  return undefined;
};

/**
 * Resolves the poll slug the same way `usePostUserFeedback` does today:
 * `/poll1/<slug>` or `/poll2/<slug>` (index 2 after a leading-slash split).
 *
 * `resumeData:v<version>:<pollAsset>` localStorage keys are NOT a fallback
 * here -- `resumeDataKey()` (`apps/trendaro-client/src/utils/localStorage.ts`)
 * encodes the poll ASSET, not the slug, and the two are not interchangeable
 * (an asset can back several slugs across accounts). Returning a slug guessed
 * from that key would silently mislabel the feedback report, so an
 * off-survey pathname resolves to `undefined` instead.
 */
export const resolveQsetSlug = (pathname: string): string | undefined => {
  const segments = pathname.split("/");
  const basePath = segments[1];
  const slug = segments[2];
  if ((basePath === "poll1" || basePath === "poll2") && slug) {
    return slug;
  }
  return undefined;
};

/**
 * The visual viewport comes in fractional CSS pixels on high-density screens
 * (`412.4166564941406`); two decimals keep a zoom or keyboard change visible
 * without the noise. `Number(...)` drops the zeros a whole value would get.
 */
const round2 = (value: number): number => Number(value.toFixed(2));

export const formatVisualViewport = (
  vv: VisualViewport | null | undefined,
): string => {
  if (!vv) return "unavailable";
  return `${round2(vv.width)}x${round2(vv.height)} offsetTop=${round2(vv.offsetTop)} scale=${round2(vv.scale)}`;
};
